// CCV/KYC presentation helpers for the proposal details drawer.
// Extracted verbatim from RentFlowsIndex.vue.
import { kycRejectionReason } from './kycOfferHistory';
import { formatDate, formatDeadlineDate } from './offerFormatters';
import { contractDeadlineValue } from './offerReadModel';

export const contractDocumentLabel = contract =>
  contract?.external_doc_token
    ? 'Documento ZapSign vinculado'
    : 'Sem documento vinculado';

export const contractStatusLabel = status =>
  ({
    pending: 'Aguardando geração',
    sent: 'Enviado',
    partially_signed: 'Parcialmente assinado',
    signed: 'Assinado',
    canceled: 'Cancelado',
    failed: 'Falhou',
  })[status] ||
  status ||
  'Não gerado';

export const contractStatusClass = status =>
  ({
    sent: 'bg-blue-50 text-blue-700',
    partially_signed: 'bg-amber-50 text-amber-700',
    signed: 'bg-emerald-50 text-emerald-700',
    canceled: 'bg-slate-100 text-slate-600',
    failed: 'bg-rose-50 text-rose-700',
  })[status] || 'bg-lais-bg-soft text-lais-text-muted';

export const kycStatusLabel = status => {
  const token = String(status || '').toLowerCase();
  return (
    {
      pending: 'Em análise',
      provider_pending: 'Em análise',
      manual_review: 'Revisão manual',
      approved: 'Aprovado',
      rejected: 'Reprovado',
      refused: 'Reprovado',
      denied: 'Reprovado',
      error: 'Erro',
      failed: 'Falhou',
      expired: 'Expirado',
    }[token] ||
    status ||
    'Não enviado'
  );
};

export const kycStatusClass = status => {
  const token = String(status || '').toLowerCase();
  return (
    {
      approved: 'bg-emerald-50 text-emerald-700',
      pending: 'bg-amber-50 text-amber-700',
      provider_pending: 'bg-amber-50 text-amber-700',
      manual_review: 'bg-amber-50 text-amber-700',
      rejected: 'bg-rose-50 text-rose-700',
      refused: 'bg-rose-50 text-rose-700',
      denied: 'bg-rose-50 text-rose-700',
      error: 'bg-rose-50 text-rose-700',
      failed: 'bg-rose-50 text-rose-700',
      expired: 'bg-rose-50 text-rose-700',
    }[token] || 'bg-lais-bg-soft text-lais-text-muted'
  );
};

export const normalizeSignerRole = role => {
  const value = String(role || '').toLowerCase();
  if (['owner', 'landlord', 'seller', 'supply'].includes(value)) {
    return 'owner';
  }
  if (['tenant', 'buyer', 'renter', 'demand'].includes(value)) {
    return 'tenant';
  }
  if (['agent', 'broker'].includes(value)) return 'agent';
  return value;
};

export const signerRoleLabel = role =>
  ({
    owner: 'Proprietário',
    tenant: 'Interessado',
    buyer: 'Interessado',
    seller: 'Proprietário',
    agent: 'Corretor',
  })[role] ||
  role ||
  'Signatário';

export const signerStatusLabel = status =>
  ({
    signed: 'Assinatura concluída',
    completed: 'Assinatura concluída',
    new: 'Pendente de assinatura',
    pending: 'Pendente de assinatura',
    email_sent: 'Pendente de assinatura',
    waiting: 'Pendente de assinatura',
    refused: 'Assinatura recusada',
    rejected: 'Assinatura recusada',
  })[String(status || '').toLowerCase()] ||
  status ||
  'Pendente de assinatura';

export const signerStatusClass = status =>
  ({
    signed: 'bg-emerald-50 text-emerald-700',
    completed: 'bg-emerald-50 text-emerald-700',
    refused: 'bg-rose-50 text-rose-700',
    rejected: 'bg-rose-50 text-rose-700',
  })[String(status || '').toLowerCase()] || 'bg-amber-50 text-amber-700';

export const isSignerSigned = signer =>
  ['signed', 'completed'].includes(String(signer?.status || '').toLowerCase());

export const signerDeadlineValue = (signer, offer) =>
  signer?.deadline_at ||
  signer?.deadline ||
  signer?.signature_deadline_at ||
  signer?.signature_deadline_date ||
  contractDeadlineValue(offer);

export const signerDescription = (signer, offer) => {
  const role = normalizeSignerRole(signer.role || signer.qualification);
  if (isSignerSigned(signer)) {
    return role === 'owner'
      ? 'Proprietário finalizou a assinatura do contrato'
      : 'Interessado finalizou a assinatura do contrato';
  }

  const deadline = signerDeadlineValue(signer, offer);
  const deadlineText = deadline
    ? ` Tem até ${formatDeadlineDate(deadline)} para assinar.`
    : '';
  if (role === 'owner') {
    return `Proprietário precisa assinar o contrato.${deadlineText}`;
  }
  if (role === 'tenant') {
    return `Interessado precisa assinar o contrato.${deadlineText}`;
  }
  return `Signatário precisa assinar o contrato.${deadlineText}`;
};

export const signerActionLabel = signer => {
  if (isSignerSigned(signer)) return 'Ver assinatura';
  const role = normalizeSignerRole(signer.role || signer.qualification);
  if (role === 'owner') return 'Assinar como proprietário';
  if (role === 'tenant') return 'Assinar como interessado';
  return 'Abrir assinatura';
};

export const contractSignerRows = (contract, offer) =>
  Array.isArray(contract?.signers)
    ? contract.signers.map((signer, index) => ({
        key: signer.token || signer.external_id || `${signer.role}-${index}`,
        role: normalizeSignerRole(signer.role || signer.qualification),
        label: signerRoleLabel(
          normalizeSignerRole(signer.role || signer.qualification)
        ),
        status: signer.status || 'new',
        statusLabel: signerStatusLabel(signer.status),
        statusClass: signerStatusClass(signer.status),
        description: signerDescription(signer, offer),
        actionLabel: signerActionLabel(signer),
        signUrl: signer.sign_url,
      }))
    : [];

export const contractProviderName = (contract, providerStatus) =>
  contract?.provider || providerStatus?.provider || 'zapsign';

export const contractProviderStatusLabel = status =>
  ({
    healthy: 'Provider conectado',
    unhealthy: 'Provider com pendência',
    unavailable: 'Provider indisponível',
  })[String(status || '').toLowerCase()] ||
  status ||
  'Provider não verificado';

export const contractProviderAlert = (contract, providerStatus) => {
  if (contract?.status === 'failed') {
    return {
      title: 'Erro provider',
      description:
        contract.last_error ||
        'O provider de assinatura retornou uma falha. Sincronize novamente ou gere um novo CCV depois de revisar a integração.',
    };
  }

  const status = String(providerStatus?.status || '').toLowerCase();
  if (
    providerStatus &&
    (providerStatus.active === false ||
      ['unhealthy', 'unavailable'].includes(status))
  ) {
    return {
      title: 'Erro provider',
      description:
        providerStatus.message ||
        'A integração de assinatura digital precisa ser revisada antes de gerar ou sincronizar o CCV.',
    };
  }

  return null;
};

export const contractActivityRows = (contract, providerStatus) =>
  [
    {
      label: 'Provider',
      value: contractProviderName(contract, providerStatus),
    },
    {
      label: 'Última sincronização',
      value: formatDate(contract?.last_synced_at),
    },
    {
      label: 'Enviado em',
      value: formatDate(contract?.sent_at),
    },
    {
      label: 'Assinado em',
      value: formatDate(contract?.signed_at),
    },
    {
      label: 'Cancelado em',
      value: formatDate(contract?.canceled_at),
    },
  ].filter(row => row.value && row.value !== '—');

export const kycProviderErrorMessage = verification => {
  const status = String(verification?.status || '').toLowerCase();
  if (!['error', 'failed'].includes(status)) return '';

  return (
    verification.failure_reason ||
    verification.result?.error_message ||
    verification.result?.message ||
    verification.metadata?.last_error ||
    kycRejectionReason(verification) ||
    'O motor de KYC retornou erro. Sincronize o status ou reenvie os documentos.'
  );
};
